import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Offcanvas, Button } from 'react-bootstrap';
import styles from './Search.module.scss';
import SearchBox from './searchBox/SearchBox';
import SearchFilters from './searchFilters/SearchFilters';
import ResultsHeader from './searchResults/resultsHeader/ResultsHeader';
import ResultsGeneral from './searchResults/resultsGeneral/ResultsGeneral';
import ResultsCollapse from './searchResults/resultsCollapse/ResultsCollapse';
import ResultsPagination from './searchResults/resultsPagination/ResultsPagination';
import { ISearchRequest, ISelectedFilters, SearchData, FilterOption, SortOption, ViewMode, AppHashState, IScopeQueries, FieldMap } from './types/SearchData';
import { SearchApi } from 'Interfaces/services/ApiService';
import { GetQueryParam } from 'Interfaces/utils/Utils';
import { viewModes } from './helpers/Const';
import { clearTag, GetSortOptionValue } from './helpers/Utils';
import { XIcon } from '../../Interfaces/Icons';

const initialSearchRequest: ISearchRequest = {
    culture: "he-il",
    query: "",
    selectedFilters: {},
    filterOptions: [],
    sortBy: "",
    nodeId: "",
    currentPageId: "",
    scopeQueries: {},
    fieldsMapping: []
};

interface SearchProps {
    noResultsMessage: string;
    viewModeOptions: ViewMode[];
    sortOptions: SortOption[];
    resultsPerPageOptions: string[];
    searchBoxPlaceholderText: string;
    filterOptions: FilterOption[];
    labels: { [key: string]: string };
    currentPageId: string;
    scopeQueries: IScopeQueries;
    fieldsMapping: FieldMap[];
}

const Search: React.FC<SearchProps> = ({
    noResultsMessage,
    viewModeOptions,
    sortOptions,
    resultsPerPageOptions,
    searchBoxPlaceholderText,
    filterOptions,
    labels,
    currentPageId,
    scopeQueries,
    fieldsMapping
}) => {
    // Get search parameters from URL hash
    const getSearchParamsFromHash = () => {
        try {
            const hash = window.location.hash.substring(1); // Remove '#'
            if (!hash) return null;

            const hashParams = new URLSearchParams(hash);
            const stateParam = hashParams.get('state');

            if (stateParam) {
                return JSON.parse(stateParam);
            }
            return null;
        } catch (error) {
            console.error('Error parsing hash parameters:', error);
            return null;
        }
    };

    const hashParams: AppHashState = getSearchParamsFromHash();
    const queryPhrase = hashParams?.q || GetQueryParam('q', null);

    const [selectedViewMode, setSelectedViewMode] = useState<ViewMode>(viewModeOptions && viewModeOptions.length > 0 ? viewModeOptions[0] : null);
    const [selectedResultsPerPage, setSelectedResultsPerPage] = useState<number>(resultsPerPageOptions && resultsPerPageOptions.length > 0 ? parseInt(resultsPerPageOptions.at(0)) : 10);
    const [selectedSortOption, setSelectedSortOption] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [searchRequest, setSearchRequest] = useState<ISearchRequest>({
        ...initialSearchRequest,
        query: queryPhrase || "",
        filterOptions: filterOptions || [],
        selectedFilters: hashParams?.filters || {},
        nodeId: hashParams?.id || "",
        currentPageId,
        scopeQueries,
        fieldsMapping
    });
    const [selectedFilters, setSelectedFilters] = useState<ISelectedFilters>(hashParams?.filters || {});
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 992);
    const [searchData, setSearchData] = useState<SearchData | null>(null);
    const [isSearching, setIsSearching] = useState<boolean>(true);

    const isFirstRender = useRef(true);

    const updateSearchRequest = (updates: Partial<ISearchRequest>) => {
        setSearchRequest((prev) => ({
            ...prev,
            ...updates,
        }));
    };

    useEffect(() => {
        const selectedSort = sortOptions?.at(selectedSortOption);
        const sortValue = selectedSort ? GetSortOptionValue(selectedSort) : "";
        const updateAndSearch = async () => {
            await updateSearchRequest({ sortBy: sortValue });
            handleSearch();
        };

        updateAndSearch();
    }, [selectedSortOption, selectedFilters]);

    const totalResults = searchData?.results?.length || 0;
    const filteredResults = searchData
        ? getFilteredResults(searchData.results, selectedResultsPerPage, currentPage)
        : [];

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedResultsPerPage]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 992);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!isFirstRender.current) {
            // No automatic search here
        } else {
            isFirstRender.current = false;
        }
    }, [searchRequest.query]);

    // Listen for hash changes and update search parameters
    useEffect(() => {
        const handleHashChange = () => {
            const newHashParams: AppHashState = getSearchParamsFromHash();
            if (newHashParams) {
                const newQuery = newHashParams.q || "";
                const newFilters = newHashParams.filters || {};
                const newNodeId = newHashParams.id || "";

                    // Update search request with new parameters from hash
                    setSearchRequest((prev) => ({
                        ...prev,
                        query: newQuery,
                        selectedFilters: newFilters,
                        nodeId: newNodeId
                    }));
                    setSelectedFilters(newFilters);
            }
        };

        // Add event listener for hash changes
        window.addEventListener('hashchange', handleHashChange);
        
        // Cleanup: Remove event listener on component unmount
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [searchRequest.query, searchRequest.selectedFilters, searchRequest.nodeId]);

    const handleSearch = async (forcedQuery?: string) => {
        setIsSearching(true); // Show preloader
        try {
            const selectedSort = sortOptions?.at(selectedSortOption);
            const sortValue = selectedSort ? GetSortOptionValue(selectedSort) : "";
            let updatedSearchRequest = { 
                ...searchRequest,
                sortBy: sortValue,
                query: forcedQuery != null ? forcedQuery : searchRequest.query 
            };
            // Update URL hash with search parameters
            const hashParams = new URLSearchParams();
            const searchParams: AppHashState = {
                ...(updatedSearchRequest.query && { q: updatedSearchRequest.query }),
                ...(updatedSearchRequest.selectedFilters &&
                    Object.keys(updatedSearchRequest.selectedFilters).length > 0 &&
                    Object.values(updatedSearchRequest.selectedFilters).some((value) =>
                        Array.isArray(value) ? value.length > 0 && value.some((v) => v !== "") : value !== ""
                    ) && { filters: updatedSearchRequest.selectedFilters }),
            };
            if (window.location.hash == "" && searchParams && Object.keys(searchParams).length > 0) {
                hashParams.set('state', JSON.stringify(searchParams));
            }
            else if (window.location.hash != ""){
                hashParams.set('state', JSON.stringify(searchParams));
            }
            window.location.hash = hashParams.toString();

            const response = await SearchApi(updatedSearchRequest);
            setSearchData(response);
            setCurrentPage(1);
        } catch (error) {
            console.error('Search API Error:', error);
            setSearchData({
                filters: [],
                results: []
            });
            setCurrentPage(1);
      } finally {
            setIsSearching(false); // Hide preloader
        }
    };

    const updateSelectedFilters = (filters: { [key: string]: string | string[] }) => {
        const normalizedFilters: ISelectedFilters = Object.keys(filters).reduce((acc, key) => {
            const value = filters[key];
            if (key == "keywords") {
                acc[key] = Array.isArray(value) ? value.map(k => clearTag(k)) : [clearTag(value)];
            }
            else {
                acc[key] = Array.isArray(value) ? value : [value];
            }
            return acc;
        }, {} as ISelectedFilters);

        updateSearchRequest({ selectedFilters: { ...searchRequest.selectedFilters, ...normalizedFilters } });
        setSelectedFilters((prev) => ({ ...prev, ...normalizedFilters }));
    };

    const updatePhrase = (phrase: string) => {
        updateSearchRequest({ query: phrase });
    };

    const clearFilter = (filterId: string, value: string) => {
        const updatedFilters = { ...searchRequest.selectedFilters };
        const currentValue = updatedFilters[filterId];

        if (Array.isArray(currentValue)) {
            updatedFilters[filterId] = currentValue.filter((item) => item !== value);
        }

        updateSelectedFilters(updatedFilters);
    };

    const handleShowFilters = () => setShowFilters(true);
    const handleCloseFilters = () => setShowFilters(false);

    return (
        <div className={`${styles["search-container"]}`}>
            {isSearching && <div id="preloader"></div>}
            <div className="row">
                {!isMobile && (
                    <div className="col-lg-4">
                        <SearchFilters
                            filters={searchData?.filters || []}
                            selectedFilters={searchRequest.selectedFilters}
                            setSelectedFilters={updateSelectedFilters}
                            labels={labels}
                            isMobile={isMobile}
                        />
                    </div>
                )}
                <div className={`col-lg-${!isMobile ? 8 : 12}`}>
                    <SearchBox
                        placeholderText={searchBoxPlaceholderText}
                        phrase={searchRequest.query}
                        setPhrase={updatePhrase}
                        triggerSearch={handleSearch}
                    />
                    <ResultsHeader
                        viewModeOptions={viewModeOptions}
                        itemsPerPageOptions={resultsPerPageOptions}
                        sortOptions={sortOptions}
                        selectedViewMode={selectedViewMode}
                        setSelectedViewMode={setSelectedViewMode}
                        selectedResultsPerPage={selectedResultsPerPage}
                        setSelectedResultsPerPage={setSelectedResultsPerPage}
                        selectedSortOption={selectedSortOption}
                        setSelectedSortOption={setSelectedSortOption}
                        totalResults={totalResults}
                        selectedFilters={selectedFilters}
                        clearFilter={clearFilter}
                        handleShowFilters={handleShowFilters}
                        labels={labels}
                        showFilters={isMobile}
                        isMobile={isMobile}
                    />
                    {totalResults === 0 && !isSearching ? (
                        <div
                            className={styles["no-results"]}
                            dangerouslySetInnerHTML={{ __html: noResultsMessage }}
                        ></div>
                    ) : selectedViewMode?.mode === viewModes.Collapse ? (
                            <ResultsCollapse results={filteredResults} labels={labels} currentPage={currentPage} selectedFilters={selectedFilters} selectedViewMode={selectedViewMode} />
                    ) : (
                        <ResultsGeneral results={filteredResults} labels={labels} layout={selectedViewMode?.mode === viewModes.Grid ? 'grid' : 'list'} />
                    )}
                    {totalResults > selectedResultsPerPage && (
                        <ResultsPagination
                            totalResults={totalResults}
                            itemsPerPage={selectedResultsPerPage}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                        />
                    )}
                </div>
            </div>

            {isMobile && (
                <Offcanvas className={styles["offcanvas-filters"]} show={showFilters} onHide={handleCloseFilters} placement="start">
                    <Offcanvas.Header>
                        <Offcanvas.Title className="pr-3 pl-3 pt-3 m-0">
                            <button type="button" className={styles["close-button"]} data-dismiss="offcanvas" aria-label="Close" onClick={handleCloseFilters}>
                                <XIcon />
                            </button>
                            {labels["FiltersTitleLabel"] &&
                                <h2 className={styles["filters-title-label"]}>{labels["FiltersTitleLabel"]}</h2>
                            }
                        </Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <SearchFilters
                            filters={searchData?.filters || []}
                            selectedFilters={searchRequest.selectedFilters}
                            setSelectedFilters={updateSelectedFilters}
                            labels={labels}
                            isMobile={isMobile}
                        />
                    </Offcanvas.Body>
                </Offcanvas>
            )}
        </div>
    );
};

const getFilteredResults = (
    results: SearchData["results"],
    itemsPerPage: number,
    currentPage: number
) => {
    return results?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
};

export const mountSearchComponent = (elementId: string, props: SearchProps) => {
    const container = document.getElementById(elementId);
    if (container) {
        const root = createRoot(container); // Use createRoot for React 18+
        root.render(<Search {...props} />);
    } else {
        console.error(`Element with ID '${elementId}' not found.`);
    }
};

// Attach the function to the global window object
if (typeof window !== 'undefined') {
    (window as any).mountSearchComponent = mountSearchComponent;
}

export default Search;